import { create } from 'zustand';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  status?: string;
  plan_key?: string;
  created_at?: string;
  trial_ends_at?: string;
  onboarding_completed?: boolean;
  onboarding_completed_at?: string | null;
}

type OrgState = {
  orgs: Organization[];
  activeOrgId: string | null;
  isLoading: boolean;
  error: string | null;
  initialized: boolean;
};

type OrgActions = {
  fetchOrgs: () => Promise<void>;
  setActiveOrgId: (orgId: string) => void;
};

const getStoredActive = (): string | null => {
  try {
    return localStorage.getItem('ima_active_org_id');
  } catch {
    return null;
  }
};

const setStoredActive = (orgId: string | null) => {
  try {
    if (orgId) localStorage.setItem('ima_active_org_id', orgId);
    else localStorage.removeItem('ima_active_org_id');
  } catch {}
};

/**
 * Flush every org-scoped store so stale data from the previous workspace
 * never bleeds into the newly-selected one.
 * Called from setActiveOrgId (fire-and-forget) and reusable from logout.
 */
export async function resetOrgState() {
  const [{ useCreditStore }, { usePaywallStore }, { useAppStore }] =
    await Promise.all([
      import('./creditStore'),
      import('./paywallStore'),
      import('./appStore'),
    ]);

  // Wipe credit balance (nulls lastFetched, bypassing the 30s stale guard)
  useCreditStore.getState().reset();

  // Close paywall modal + unlock canvas
  usePaywallStore.setState({
    isModalOpen: false,
    triggerSource: null,
    paywallVariant: null,
    isCanvasLocked: false,
    canvasDismissed: false,
  });

  // Wipe gallery (org-scoped images)
  useAppStore.setState({
    galleryImages: [],
    galleryTotal: 0,
    galleryPage: 1,
    galleryHasMore: false,
    galleryLoadingMore: false,
    isLoading: false,
    error: null,
  });
}

export const useOrgStore = create<OrgState & OrgActions>((set, get) => ({
  orgs: [],
  activeOrgId: getStoredActive(),
  isLoading: false,
  error: null,
  initialized: false,

  fetchOrgs: async () => {
    try {
      set({ isLoading: true, error: null });
      const res = await fetch('/api/orgs', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch organizations');
      const payload = await res.json();
      const orgs: Organization[] = payload?.data || [];
      let active = get().activeOrgId;
      if (!active || !orgs.some(o => o.id === active)) {
        active = orgs[0]?.id || null;
        setStoredActive(active);
      }
      set({ orgs, activeOrgId: active, isLoading: false, initialized: true });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to fetch orgs', isLoading: false, initialized: true });
    }
  },

  setActiveOrgId: (orgId: string) => {
    const prev = get().activeOrgId;
    if (prev === orgId) return;
    setStoredActive(orgId);
    set({ activeOrgId: orgId });
    resetOrgState();
  },
}));

export const getActiveOrgId = (): string | null => {
  try {
    return useOrgStore.getState().activeOrgId || getStoredActive();
  } catch {
    return getStoredActive();
  }
};


